import { useState, useEffect, useMemo } from "react";
import { SelectChangeEvent } from "@mui/material";
import { Reservation } from "../types/Reservation";
import { useUpdateReservation } from "../../../hooks/useReservationMutations";
import { useReservaValidacion } from "../hooks/useReservaValidacion";
import { useZonas } from "../../calendar/hooks/useZonas";
import { CalculationService } from "../../../services/calculationService";
import { logger } from "../../../utils/logger";
import {
  validarEmail,
  validarTelefono,
  validarDNI,
  validarDNIPersonaDirecto,
  ERROR_DOCUMENTO,
} from "../../shared/utils/validaciones";
import { parseMonetaryValue } from "../../shared/utils/formatters";
import { formatDateKey } from "../../shared/utils/dateUtils";

interface FormData {
  entrada: Date | null;
  salida: Date | null;
  departamento: string;
  nombre: string;
  apellido: string;
  dni: string;
  valor_total: string;
  pago: string;
  ajuste_financiero: string;
  tipo_ajuste: string;
  cantidad_huespedes: string;
  tipo_cama: string[];
  localidad: string;
  email1: string;
  email2: string;
  telefono1: string;
  telefono2: string;
  acompanantes: Array<{
    nombre: string;
    apellido: string;
    dni: string;
    edad: string;
  }>;
  afiliado_nombre: string;
  afiliado_apellido: string;
  afiliado_dni: string;
  late_checkout: boolean;
  politica_precio?: string;
  precio_personalizado?: string;
  tipo_precio_personalizado?: string;
  usar_limpiezas_manuales: boolean;
  sin_limpieza_automatica: boolean;
  dias_limpieza_pactados: string[];
  dias_limpieza_recambio: string[];
}

interface FormErrors {
  entrada?: string;
  salida?: string;
  departamento?: string;
  nombre?: string;
  apellido?: string;
  dni?: string;
  valor_total?: string;
  pago?: string;
  cantidad_huespedes?: string;
  email1?: string;
  email2?: string;
  telefono1?: string;
  telefono2?: string;
  edad?: string;
  overlap?: string;
}

interface PendingDepartmentChange {
  newDepartment: string;
  currentPrice: number;
  newPrice: number;
  pricePerNight: number;
  newPricePerNight: number;
  newPriceSpecial?: number;
  newPricePerNightSpecial?: number;
}

export const useEditReservation = (
  selectedReservation: Reservation | null,
  existingReservations: Reservation[]
) => {
  const [activeTab, setActiveTab] = useState(0);
  const { checkOverlap, overlapError } = useReservaValidacion();
  const updateReservationMutation = useUpdateReservation();
  const [formData, setFormData] = useState<FormData>({
    entrada: null,
    salida: null,
    departamento: "",
    nombre: "",
    apellido: "",
    dni: "",
    valor_total: "",
    pago: "",
    ajuste_financiero: "",
    tipo_ajuste: "ninguno",
    cantidad_huespedes: "",
    tipo_cama: selectedReservation?.tipo_cama
      ? Array.isArray(selectedReservation.tipo_cama)
        ? selectedReservation.tipo_cama
        : [selectedReservation.tipo_cama]
      : ["doble"],
    localidad: "",
    email1: "",
    email2: "",
    telefono1: "",
    telefono2: "",
    acompanantes: [],
    afiliado_nombre: "",
    afiliado_apellido: "",
    afiliado_dni: "",
    late_checkout: false,
    usar_limpiezas_manuales: false,
    sin_limpieza_automatica: false,
    dias_limpieza_pactados: [],
    dias_limpieza_recambio: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const { zonas, error: errorZonas } = useZonas();

  // Estados para modal de confirmación de precio especial
  const [showPriceConfirmModal, setShowPriceConfirmModal] = useState(false);
  const [pendingDepartmentChange, setPendingDepartmentChange] =
    useState<PendingDepartmentChange | null>(null);
  const [priceModalShown, setPriceModalShown] = useState(false); // Control para evitar mostrar el modal múltiples veces
  const [precioConfirmadoPorUsuario, setPrecioConfirmadoPorUsuario] =
    useState(false); // Evitar recálculo automático

  // Estado para el botón "Marcar como Pagado"
  const esPagado = useMemo(() => {
    const valorTotal = parseMonetaryValue(formData.valor_total);
    const pago = parseMonetaryValue(formData.pago);
    // Permitir sobrepagos: el botón se habilita cuando pago >= valor_total
    return pago >= valorTotal && valorTotal > 0;
  }, [formData.valor_total, formData.pago]);

  // Actualizar el formulario cuando cambia la reserva seleccionada
  useEffect(() => {
    const inicializarFormulario = async () => {
      if (selectedReservation) {
        const entradaDate = new Date(selectedReservation.entrada + "T00:00:00");
        const salidaDate = new Date(selectedReservation.salida + "T00:00:00");

        // Usar valor_total (precio actual) para mostrar el precio correcto
        let valorTotalInicial = selectedReservation.valor_total || 0;

        // Solo calcular dinámicamente si no hay valor_original (reservas antiguas)
        if (
          !selectedReservation.valor_original ||
          selectedReservation.valor_original === 0
        ) {
          try {
            // Usar el backend para calcular el valor total
            const resultado = await CalculationService.calcularValorTotal({
              entrada: formatDateKey(entradaDate),
              salida: formatDateKey(salidaDate),
              departamento: selectedReservation.departamento,
              late_checkout: selectedReservation.late_checkout || false,
            });
            valorTotalInicial = resultado.valor_total;
          } catch (error) {
            logger.error("Error al calcular valor total inicial:", error);
            // Fallback: usar valor_total existente
            valorTotalInicial = selectedReservation.valor_total || 0;
          }
        }

        const formDataInicial = {
          entrada: entradaDate,
          salida: salidaDate,
          departamento: selectedReservation.departamento || "",
          nombre: selectedReservation.nombre || "",
          apellido: selectedReservation.apellido || "",
          dni: selectedReservation.dni || "",
          valor_total: valorTotalInicial.toString(),
          pago:
            selectedReservation.pago !== undefined &&
            selectedReservation.pago !== null
              ? selectedReservation.pago.toString()
              : "0",
          ajuste_financiero:
            selectedReservation.ajuste_financiero !== undefined &&
            selectedReservation.ajuste_financiero !== null
              ? selectedReservation.ajuste_financiero.toString()
              : "0",
          tipo_ajuste: selectedReservation.tipo_ajuste || "ninguno",
          cantidad_huespedes: selectedReservation.cantidad_huespedes
            ? selectedReservation.cantidad_huespedes.toString()
            : "1",
          tipo_cama: selectedReservation.tipo_cama
            ? Array.isArray(selectedReservation.tipo_cama)
              ? selectedReservation.tipo_cama
              : [selectedReservation.tipo_cama]
            : ["doble"],
          localidad: selectedReservation.localidad || "",
          email1: selectedReservation.email1 || "",
          email2: selectedReservation.email2 || "",
          telefono1: selectedReservation.telefono1 || "",
          telefono2: selectedReservation.telefono2 || "",
          acompanantes: Array.isArray(selectedReservation.acompanantes)
            ? selectedReservation.acompanantes.map((acomp) => ({
                ...acomp,
                edad: acomp.edad.toString(),
              }))
            : [],
          afiliado_nombre: selectedReservation.afiliado_nombre || "",
          afiliado_apellido: selectedReservation.afiliado_apellido || "",
          afiliado_dni: selectedReservation.afiliado_dni || "",
          late_checkout: selectedReservation.late_checkout || false,
          sin_limpieza_automatica:
            selectedReservation.sin_limpieza_automatica || false,
          usar_limpiezas_manuales:
            selectedReservation.dias_limpieza_pactados &&
            selectedReservation.dias_limpieza_pactados.length > 0,
          dias_limpieza_pactados:
            selectedReservation.dias_limpieza_pactados || [],
          dias_limpieza_recambio:
            selectedReservation.dias_limpieza_recambio || [],
        };

        setFormData(formDataInicial);
        setPriceModalShown(false); // Resetear el estado del modal al cambiar de reserva
      }
    };

    inicializarFormulario();
  }, [selectedReservation]);

  const handleDateChange = (date: Date | null, name: "entrada" | "salida") => {
    if (date) {
      setFormData((prev: FormData) => {
        const newData = { ...prev, [name]: date };
        if (newData.entrada && newData.salida && newData.departamento) {
          const reservaTemporal: Reservation = {
            id: selectedReservation?.id || 0,
            nombre: newData.nombre || "",
            departamento: newData.departamento,
            entrada: formatDateKey(newData.entrada),
            salida: formatDateKey(newData.salida),
            estado: "confirmada",
            pago: 0,
          };
          checkOverlap(reservaTemporal, existingReservations, true);
        }
        return newData;
      });
      // Resetear bandera para permitir recálculo cuando cambian las fechas
      setPrecioConfirmadoPorUsuario(false);
    }
  };

  const handleTextChange = (
    e: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target;
    let error = "";

    // Manejo especial para cambio de departamento
    if (name === "departamento") {
      // Validar superposición ANTES de mostrar modal de cambio de departamento
      const reservaTemporal: Reservation = {
        id: selectedReservation?.id || 0,
        nombre: formData.nombre || "",
        departamento: value, // Nuevo departamento
        entrada: formData.entrada ? formatDateKey(formData.entrada) : "",
        salida: formData.salida ? formatDateKey(formData.salida) : "",
        estado: "confirmada",
        apellido: formData.apellido || "",
        dni: formData.dni || "",
        localidad: formData.localidad || "",
        email1: formData.email1 || "",
        email2: formData.email2 || "",
        telefono1: formData.telefono1 || "",
        telefono2: formData.telefono2 || "",
        cantidad_huespedes: Number(formData.cantidad_huespedes) || 1,
        tipo_cama: formData.tipo_cama || ["doble"],
        acompanantes: (formData.acompanantes || []).map((acompanante) => ({
          ...acompanante,
          edad: Number(acompanante.edad) || 0,
        })),
        afiliado_nombre: formData.afiliado_nombre || "",
        afiliado_apellido: formData.afiliado_apellido || "",
        afiliado_dni: formData.afiliado_dni || "",
        late_checkout: formData.late_checkout || false,
        valor_total: Number(formData.valor_total) || 0,
        pago: Number(formData.pago) || 0,
        tipo_ajuste: formData.tipo_ajuste || "ninguno",
        // NO incluir ajuste_financiero para que el backend lo calcule automáticamente
        precio_personalizado: formData.precio_personalizado
          ? Number(formData.precio_personalizado)
          : undefined,
        hora_inicio: "",
        hora_fin: "",
        notas: "",
      };

      // Verificar superposición con el nuevo departamento
      const haySuperposicion = checkOverlap(
        reservaTemporal,
        existingReservations,
        true
      );

      if (haySuperposicion) {
        // Si hay superposición, no mostrar modal de cambio de departamento
        logger.warn(
          "🚫 Superposición detectada al cambiar departamento - cancelando cambio"
        );
        return; // No hacer nada, mantener el departamento actual
      }

      // Si no hay superposición, proceder con el cambio de departamento
      handleDepartamentoChange(value);
      return; // Salir temprano para evitar actualizar formData aquí
    }

    if (name === "dni") {
      const origen = selectedReservation?.cliente?.origen_reserva;
      const esReservaExterna = origen === "booking" || origen === "airbnb";
      if (esReservaExterna) {
        if (value.trim().length === 0) {
          error =
            origen === "booking"
              ? "El código de reserva Booking es obligatorio"
              : "El código de reserva Airbnb es obligatorio";
        }
      } else if (!validarDNIPersonaDirecto(value) && !validarDNI(value)) {
        error = ERROR_DOCUMENTO;
      }
    } else if (name === "email1" || name === "email2") {
      if (value && !validarEmail(value)) {
        error = "El email no tiene un formato válido";
      }
    } else if (name === "telefono1" || name === "telefono2") {
      if (value && !validarTelefono(value)) {
        error = "El teléfono no tiene un formato válido";
      }
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
    setFormData((prev: FormData) => ({ ...prev, [name]: value }));
  };

  // Función específica para manejar cambios de departamento
  const handleDepartamentoChange = async (newDepartment: string) => {
    if (!selectedReservation || !formData.entrada || !formData.salida) {
      setFormData((prev) => ({ ...prev, departamento: newDepartment }));
      return;
    }

    // Verificar si la reserva original tiene precio especial
    logger.debug("🔍 Debug precio especial:", {
      precio_personalizado: selectedReservation.precio_personalizado,
      valor_original: selectedReservation.valor_original,
      valor_total: selectedReservation.valor_total,
      tipo_precio_personalizado:
        typeof selectedReservation.precio_personalizado,
    });

    // Solo mostrar modal si hay precio especial o si el valor del nuevo departamento es diferente
    if (newDepartment !== selectedReservation.departamento) {
      try {
        // Calcular noches (no se usa pero se mantiene para consistencia)
        // const noches = Math.max(
        //   1,
        //   Math.round(
        //     (formData.salida.getTime() - formData.entrada.getTime()) /
        //       (1000 * 60 * 60 * 24)
        //   )
        // );

        // Precio actual (con late checkout si aplica)
        const precioActual = parseMonetaryValue(formData.valor_total);

        // Calcular precio base del nuevo departamento.
        const resultadoNuevoPrecioBase =
          await CalculationService.calcularValorTotal({
            entrada: formatDateKey(formData.entrada),
            salida: formatDateKey(formData.salida),
            departamento: newDepartment,
            late_checkout: formData.late_checkout || false,
            precio_personalizado: selectedReservation.precio_personalizado
              ? Number(selectedReservation.precio_personalizado)
              : undefined,
            usar_precio_base: true,
          });

        // Calcular precio con el precio personalizado actual.
        const resultadoNuevoPrecioEspecial =
          await CalculationService.calcularValorTotal({
            entrada: formatDateKey(formData.entrada),
            salida: formatDateKey(formData.salida),
            departamento: newDepartment,
            late_checkout: formData.late_checkout || false,
            precio_personalizado: selectedReservation.precio_personalizado
              ? Number(selectedReservation.precio_personalizado)
              : undefined,
          });

        const precioNuevoBase = resultadoNuevoPrecioBase.valor_total;
        const precioPorNocheNuevoBase =
          resultadoNuevoPrecioBase.precio_por_noche;

        const precioNuevoEspecial = resultadoNuevoPrecioEspecial.valor_total;
        const precioPorNocheNuevoEspecial =
          resultadoNuevoPrecioEspecial.precio_por_noche;

        // Calcular precio por noche actual SIN late checkout para mostrar precio base
        const resultadoActual = await CalculationService.calcularValorTotal({
          entrada: formatDateKey(formData.entrada),
          salida: formatDateKey(formData.salida),
          departamento: selectedReservation.departamento,
          late_checkout: false, // SIN late checkout para mostrar precio base por noche
          precio_personalizado: selectedReservation.precio_personalizado
            ? Number(selectedReservation.precio_personalizado)
            : undefined,
        });

        const precioPorNocheActual = resultadoActual.precio_por_noche;

        // Verificar si hay precio especial o si el valor es diferente
        const precioPersonalizado =
          Number(selectedReservation.precio_personalizado) || 0;
        const hayPrecioEspecial = precioPersonalizado > 0;
        const valorEsDiferenteBase =
          Math.abs(precioActual - precioNuevoBase) > 1; // Tolerancia de 1 peso
        const valorEsDiferenteEspecial =
          Math.abs(precioActual - precioNuevoEspecial) > 1;

        logger.debug("🔍 Comparación de precios:", {
          hayPrecioEspecial,
          valorEsDiferenteBase,
          valorEsDiferenteEspecial,
          precioActual,
          precioNuevoBase,
          precioNuevoEspecial,
          diferenciaBase: Math.abs(precioActual - precioNuevoBase),
          diferenciaEspecial: Math.abs(precioActual - precioNuevoEspecial),
        });

        // Solo mostrar modal si hay precio especial o si el valor es diferente
        if (hayPrecioEspecial || valorEsDiferenteBase) {
          setPendingDepartmentChange({
            newDepartment,
            currentPrice: precioActual, // Precio total actual (con late checkout si aplica)
            newPrice: precioNuevoBase,
            newPriceSpecial: precioNuevoEspecial,
            pricePerNight: precioPorNocheActual, // Precio por noche del departamento actual
            newPricePerNight: precioPorNocheNuevoBase,
            newPricePerNightSpecial: precioPorNocheNuevoEspecial,
          });
          setShowPriceConfirmModal(true);
          setPriceModalShown(true);
        } else {
          // Los precios son iguales y no hay precio especial, aplicar cambio directamente
          logger.debug("🔍 Precios iguales - aplicando cambio directamente");
          setFormData((prev) => ({ ...prev, departamento: newDepartment }));
        }
      } catch (error) {
        logger.error("Error al verificar precio especial:", error);
        // Si hay error, aplicar el cambio directamente
        setFormData((prev) => ({ ...prev, departamento: newDepartment }));
      }
    } else {
      // No hay precio especial, aplicar cambio directamente
      logger.debug(
        "🔍 No se mostró modal de precio especial - aplicando cambio directamente"
      );
      setFormData((prev) => ({ ...prev, departamento: newDepartment }));
    }
  };

  // Función para marcar como pagado
  const handleMarcarPagado = () => {
    const valorTotal = parseMonetaryValue(formData.valor_total);
    setFormData((prev) => ({
      ...prev,
      pago: valorTotal.toString(),
    }));
  };

  // Función para confirmar mantener precio especial
  const handleMantenerPrecioEspecial = () => {
    logger.debug(
      "🔍 Usuario seleccionó: Mantener precio especial",
      pendingDepartmentChange
    );
    if (pendingDepartmentChange) {
      setFormData((prev) => {
        const newFormData = {
          ...prev,
          departamento: pendingDepartmentChange.newDepartment, // Cambiar departamento
          valor_total: pendingDepartmentChange.currentPrice.toString(), // Mantener precio actual
          politica_precio: "mantener_precio_especial", // Enviar política al backend
          precio_personalizado:
            selectedReservation?.precio_personalizado?.toString() || "",
          tipo_precio_personalizado: "number", // Mantener tipo como number
        };
        logger.debug("🔍 FormData actualizado (mantener precio):", {
          departamento: newFormData.departamento,
          valor_total: newFormData.valor_total,
          politica_precio: newFormData.politica_precio,
          precio_personalizado: newFormData.precio_personalizado,
          tipo_precio_personalizado: newFormData.tipo_precio_personalizado,
        });
        return newFormData;
      });
    }
    setPrecioConfirmadoPorUsuario(true); // Marcar que el usuario confirmó el precio
    setShowPriceConfirmModal(false);
    setPendingDepartmentChange(null);
  };

  // Función para usar precio base del nuevo departamento
  const handleUsarPrecioBase = () => {
    logger.debug(
      "🔍 Usuario seleccionó: Usar precio base del nuevo departamento",
      pendingDepartmentChange
    );
    if (pendingDepartmentChange) {
      setFormData((prev) => {
        const newFormData = {
          ...prev,
          departamento: pendingDepartmentChange.newDepartment, // Cambiar departamento
          valor_total: pendingDepartmentChange.newPrice.toString(), // Actualizar precio inmediatamente
          politica_precio: "usar_precio_base_nuevo_dpto", // Enviar política al backend
          precio_personalizado: "",
          tipo_precio_personalizado: "",
        };
        logger.debug("🔍 FormData actualizado (usar precio base):", {
          departamento: newFormData.departamento,
          valor_total: newFormData.valor_total,
          politica_precio: newFormData.politica_precio,
          precio_personalizado: newFormData.precio_personalizado,
          tipo_precio_personalizado: newFormData.tipo_precio_personalizado,
        });
        return newFormData;
      });
    }
    setPrecioConfirmadoPorUsuario(true); // Marcar que el usuario confirmó el precio
    setShowPriceConfirmModal(false);
    setPendingDepartmentChange(null);
  };

  // Función para cancelar cambio de departamento
  const handleCancelarCambioDepartamento = () => {
    // Restaurar departamento original
    setFormData((prev) => ({
      ...prev,
      departamento: selectedReservation?.departamento || "",
    }));
    setPrecioConfirmadoPorUsuario(false); // Resetear bandera
    setShowPriceConfirmModal(false);
    setPendingDepartmentChange(null);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Calcular saldo pendiente usando la misma lógica que ReservationDetails
  const saldoPendiente = useMemo(() => {
    const valorTotal = parseMonetaryValue(formData.valor_total);
    const pago = parseMonetaryValue(formData.pago);
    // Usar misma fórmula que ReservationDetails: saldo = valor_total - pago
    return valorTotal - pago;
  }, [formData.valor_total, formData.pago]);

  return {
    // Estados
    activeTab,
    formData,
    setFormData,
    loading,
    setLoading,
    error,
    setError,
    errors,
    setErrors,
    zonas,
    errorZonas,
    overlapError,
    showPriceConfirmModal,
    setShowPriceConfirmModal,
    pendingDepartmentChange,
    setPendingDepartmentChange,
    priceModalShown,
    setPriceModalShown,
    precioConfirmadoPorUsuario,
    setPrecioConfirmadoPorUsuario,
    esPagado,
    saldoPendiente,

    // Funciones
    handleDateChange,
    handleTextChange,
    handleDepartamentoChange,
    handleMarcarPagado,
    handleMantenerPrecioEspecial,
    handleUsarPrecioBase,
    handleCancelarCambioDepartamento,
    handleTabChange,

    // Mutaciones
    updateReservationMutation,
  };
};
